import React from 'react'
import { Document, Font, Image, Page, Text, View } from '@react-pdf/renderer'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale/fr'
import { stylesBackPage, stylesFrontPage, stylesHealthRecordPage, stylesInitialEvaluationPage, stylesToShare } from './styles'
import { Indicator, InitialEvaluationData, MedicalOverview, PatientData, WellBeingSummary } from './types'

export interface MedicalReportPDFProps {
  patientData: PatientData
  medicalOverview: MedicalOverview
  initialEvaluationData: InitialEvaluationData
  wellBeingSummary: WellBeingSummary
  translations?: Record<string, string>
}

// Create Document Component
export const MedicalReportPDF = ({ translations, patientData, medicalOverview, initialEvaluationData, wellBeingSummary }: MedicalReportPDFProps) => {
  Font.register({ family: 'Rubik', src: '/fonts/Rubik/Rubik-Regular.ttf', fontStyle: 'normal', fontWeight: 400 })
  Font.register({ family: 'Rubik', src: '/fonts/Rubik/Rubik-Italic.ttf', fontStyle: 'italic', fontWeight: 400 })
  Font.register({ family: 'Rubik', src: '/fonts/Rubik/Rubik-Medium.ttf', fontStyle: 'normal', fontWeight: 500 })
  Font.register({ family: 'Rubik', src: '/fonts/Rubik/Rubik-Bold.ttf', fontStyle: 'normal', fontWeight: 700 })

  const getGeneralInfoElement = (title: string, value: string | undefined) => (
    <View style={stylesHealthRecordPage.patientGeneralInfoElement}>
      <Text style={stylesHealthRecordPage.patientGeneralInfoElementTitle}>{title}:</Text>
      <Text style={stylesHealthRecordPage.patientGeneralInfoElementValue}>{value ?? '-'}</Text>
    </View>
  )
  const getMedicalInfoElement = (title: string, medicalConditions: string[] = []) => (
    <View style={stylesHealthRecordPage.patientMedicalInfo}>
      <Text style={stylesHealthRecordPage.patientMedicalInfoTitle}>{title}:</Text>
      <View style={stylesHealthRecordPage.patientMedicalInfoValuesArray}>
        {medicalConditions?.length > 0 ? (
          medicalConditions.map((condition, index) => (
            <Text key={index} style={stylesHealthRecordPage.patientMedicalInfoValue}>
              {condition}
            </Text>
          ))
        ) : (
          <Text style={stylesHealthRecordPage.patientMedicalInfoValue}>- Aucun</Text>
        )}
      </View>
    </View>
  )
  const getHealthRecordPageFooterElement = (title: string, imgSrc: string) => (
    <View style={stylesHealthRecordPage.footerElement}>
      <Image style={stylesHealthRecordPage.footerElementImg} src={imgSrc} />
      <Text style={stylesHealthRecordPage.footerElementValue}>{title}</Text>
    </View>
  )
  const lighterColors: Record<string, string> = {
    '#FF4D4F': '#FFBABB',
    '#FFBE0B': '#FFE699',
    '#56C221': '#BDE8A4',
    '#ADADAD': '#D6D6D6',
  }

  const getColor = (score: number | undefined): string => {
    if (score === undefined) return '#ADADAD'
    if (score <= 30) return '#FF4D4F'
    if (score <= 70) return '#FFBE0B'
    return '#56C221'
  }

  const getInitialEvaluationElement = (healthElement: Indicator, index: number) => {
    const hasRange = healthElement.iaProgress !== undefined
    const min = hasRange ? Math.min(healthElement.progress ?? 100, healthElement.iaProgress ?? 100) : (healthElement.progress ?? 0)
    const max = hasRange ? Math.max(healthElement.progress ?? 0, healthElement.iaProgress ?? 0) : (healthElement.progress ?? 0)
    const avg = hasRange ? ((healthElement.progress ?? 0) + (healthElement.iaProgress ?? 0)) / 2 : (healthElement.progress ?? 0)

    const color = getColor(avg)
    const lighter = lighterColors[color]
    const solidWidth = (180 * min) / 100
    const lighterWidth = hasRange ? (180 * (max - min)) / 100 : 0

    return (
      <View key={index} style={stylesInitialEvaluationPage.healthElement}>
        <View style={stylesInitialEvaluationPage.healthElementIllustrations}>
          <Image style={stylesInitialEvaluationPage.healthElementIllustrationsIcn} src={healthElement.imgSrc} />
          <View style={stylesInitialEvaluationPage.healthElementHeadings}>
            <Text style={stylesInitialEvaluationPage.healthElementTitle}>{healthElement.title}</Text>
            <View style={stylesInitialEvaluationPage.healthElementProgressContainer}>
              {solidWidth > 0 && (
                <View
                  style={{
                    width: solidWidth,
                    height: '100%',
                    backgroundColor: color,
                    borderTopLeftRadius: 10,
                    borderBottomLeftRadius: 10,
                    borderTopRightRadius: lighterWidth > 0 ? 0 : 10,
                    borderBottomRightRadius: lighterWidth > 0 ? 0 : 10,
                  }}
                />
              )}
              {lighterWidth > 0 && (
                <View
                  style={{
                    width: lighterWidth,
                    height: '100%',
                    backgroundColor: lighter,
                    borderTopLeftRadius: solidWidth > 0 ? 0 : 10,
                    borderBottomLeftRadius: solidWidth > 0 ? 0 : 10,
                    borderTopRightRadius: 10,
                    borderBottomRightRadius: 10,
                  }}
                />
              )}
            </View>
          </View>
        </View>
        <View style={stylesInitialEvaluationPage.healthElementDivider}></View>
        <View style={stylesInitialEvaluationPage.healthElementDescriptionArray}>
          {healthElement.description.map((el, index) => (
            <Text key={index} style={stylesInitialEvaluationPage.healthElementDescriptionParagraph}>
              {el}
            </Text>
          ))}
        </View>
      </View>
    )
  }
  function italicize(text: string, keyPrefix: string) {
    const parts = text.split('*')
    if (parts.length === 1) return text
    return parts.map((v, idx) =>
      idx % 2 === 0 ? (
        v
      ) : (
        <Text key={`${keyPrefix}-i${idx}`} style={stylesToShare.inlineItalic}>
          {v}
        </Text>
      ),
    )
  }

  function formatText(paragraph: string) {
    const boldParts = paragraph.trim().split('**')
    //If everything is bold, nothing is bold
    if (boldParts.length === 1) return italicize(paragraph, 'r')
    if (boldParts.length === 3 && boldParts[0].length === 0 && boldParts[2].length === 0) return italicize(boldParts[1], 'r')
    return boldParts.map((v, idx) =>
      idx % 2 === 0 ? (
        italicize(v, `b${idx}`)
      ) : (
        <Text key={`b${idx}`} style={stylesToShare.inlineBold}>
          {v}
        </Text>
      ),
    )
  }

  const getWellBeingSummary = (heading: string, summary: { title?: string; description: string }[], index: number) => {
    return (
      <View style={stylesToShare.summaryContainer} key={index}>
        <Text style={stylesToShare.summaryTitle}>{heading}</Text>
        <View style={stylesToShare.summaryBody}>
          {summary.map((el, idx) => (
            <View key={idx} style={stylesToShare.summaryItem}>
              {!!el.title && <Text style={stylesToShare.summaryItemTitle}>{el.title}:</Text>}
              {el.description.split(/\n+/).map((paragraph, pIdx) =>
                paragraph.trim() === '---' ? (
                  <View key={pIdx} style={stylesToShare.horizontalSeparator} />
                ) : (
                  <Text key={pIdx} style={stylesToShare.summaryItemText}>
                    {formatText(paragraph)}
                  </Text>
                ),
              )}
            </View>
          ))}
        </View>
      </View>
    )
  }

  return patientData && medicalOverview && initialEvaluationData && wellBeingSummary ? (
    <Document title={`Rapport médical ${patientData.name}`}>
      <Page style={stylesFrontPage.frontPage}>
        <Image style={stylesFrontPage.preventiLogo} src="/report/Logo_Preventi.png" />
        <Text style={stylesFrontPage.pageTitle}>{translations?.['Rapport médical'] ?? 'Rapport médical'}</Text>
        <Image style={stylesFrontPage.frontPageIllustration} src="/report/front_page_illustration.png" />
        <Text style={stylesFrontPage.date}>
          {format(new Date(), 'dd MMMM yyyy', {
            locale: fr,
          })}
        </Text>
      </Page>
      <Page style={stylesHealthRecordPage.healthRecordPage}>
        <View style={stylesToShare.header} fixed>
          <Image style={stylesToShare.preventiLogoHeader} src="/report/Logo_Preventi.png" />
        </View>
        <View style={stylesHealthRecordPage.sideLine} fixed>
          <Image style={stylesHealthRecordPage.preventiLogoWhite} src="/report/Logo_Preventi_white.png" />
          <Image style={stylesHealthRecordPage.blocksIcn} src="/report/blocks_icn.png" />
        </View>
        <View style={stylesHealthRecordPage.main}>
          <View style={stylesToShare.content}>
            <Text style={stylesToShare.pageTitle}>{translations?.['Dossier santé'] ?? 'Dossier santé'}</Text>
            <View style={stylesHealthRecordPage.patientNameWrapper}>
              <Text style={stylesHealthRecordPage.patientName}>{patientData.name}</Text>
            </View>
            <View style={stylesHealthRecordPage.patientGeneralInfoBlock}>
              {getGeneralInfoElement(translations?.['Age'] ?? 'Age', patientData.age)}
              {getGeneralInfoElement(translations?.['Poids'] ?? 'Poids', patientData.weight)}
              {getGeneralInfoElement(translations?.['Date de naissance'] ?? 'Date de naissance', patientData.dateOfBirth)}
              {getGeneralInfoElement(translations?.['Taille'] ?? 'Taille', patientData.height)}
              {getGeneralInfoElement(translations?.['Sexe de naissance'] ?? 'Sexe de naissance', patientData.birthSex)}
              {getGeneralInfoElement(translations?.['Tension artérielle'] ?? 'Tension artérielle', patientData.bloodPressure)}
              {getGeneralInfoElement(translations?.['Groupe sanguin'] ?? 'Groupe sanguin', patientData.bloodGroup)}
              {getGeneralInfoElement(translations?.['Fréquence cardiaque'] ?? 'Fréquence cardiaque', patientData.heartRate)}
              {getGeneralInfoElement(translations?.['Tour de taille'] ?? 'Tour de taille', patientData.waistCircumference)}
              {getGeneralInfoElement(translations?.['BMI'] ?? 'BMI', patientData.bmi)}
            </View>
            <View style={stylesHealthRecordPage.patientMedicalInfoBlock}>
              {getMedicalInfoElement(translations?.['Traitement chronique'] ?? 'Traitement chronique', medicalOverview.chronicTreatment)}
              {getMedicalInfoElement(translations?.['Allergies'] ?? 'Allergies', medicalOverview.allergies)}
              {getMedicalInfoElement(translations?.['Antécédents médicaux'] ?? 'Antécédents médicaux', medicalOverview.medicalHistory)}
              {getMedicalInfoElement(translations?.['Antécédents chirurgicaux'] ?? 'Antécédents chirurgicaux', medicalOverview.surgicalHistory)}
              {getMedicalInfoElement(translations?.['Antécédents familiaux'] ?? 'Antécédents familiaux', medicalOverview.familyHistory)}
            </View>
          </View>
          <View style={stylesHealthRecordPage.footer}>
            {getHealthRecordPageFooterElement(translations?.['Bruxelles'] ?? 'Bruxelles', '/report/footer/location.png')}
            {getHealthRecordPageFooterElement(translations?.['02/411.25.23'] ?? '02/411.25.23', '/report/footer/call.png')}
            {getHealthRecordPageFooterElement(translations?.['info@preventicare.be'] ?? 'info@preventicare.be', '/report/footer/mail.png')}
          </View>
        </View>
        <Text style={stylesToShare.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>
      <Page style={stylesToShare.pageBody}>
        <View style={stylesToShare.header} fixed>
          <Image style={stylesToShare.preventiLogoHeader} src="/report/Logo_Preventi.png" />
        </View>
        <View style={stylesToShare.content}>
          <Text style={stylesToShare.pageTitle}>{translations?.['Évaluation Initiale'] ?? 'Évaluation Initiale'}</Text>
          <View style={stylesInitialEvaluationPage.healthElementBlock}>{initialEvaluationData.indicators.map((el, key) => getInitialEvaluationElement(el, key))}</View>
        </View>
        <Text style={stylesToShare.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>
      <Page style={stylesToShare.pageBody}>
        <View style={stylesToShare.header} fixed>
          <Image style={stylesToShare.preventiLogoHeader} src="/report/Logo_Preventi.png" />
        </View>
        <View style={stylesToShare.content}>
          <Text style={stylesToShare.pageTitle}>{translations?.['Recommandations'] ?? 'Recommandations'}</Text>
          {wellBeingSummary.map((el, index) => getWellBeingSummary(el.heading, el.summary, index))}
        </View>
        <Text style={stylesToShare.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>
      <Page style={stylesBackPage.pageContainer}>
        <Image style={stylesBackPage.illustration} src="/report/back_page_illustration.png" />
        <View style={stylesBackPage.footerContainer}>
          <View style={stylesBackPage.footerContentWrapper}>
            <Text style={stylesToShare.pageTitle}>Contact us</Text>
            <View style={stylesBackPage.contactList}>
              <Text style={stylesBackPage.contactItem}>Tel: 02/411.25.23</Text>
              <Text style={stylesBackPage.contactItem}>info@preventicare.be</Text>
              <Text style={stylesBackPage.contactItem}>www.preventicare.be</Text>
            </View>
          </View>
          <Image style={stylesToShare.preventiLogoHeader} src="/report/Logo_Preventi.png" />
        </View>
      </Page>
    </Document>
  ) : (
    <Document title={`${translations?.['Rapport médical'] ?? 'Rapport médical'} ${patientData.name}`}></Document>
  )
}
